import React, { createContext, useContext, useState, useEffect } from "react";

const AdminContext = createContext();

const STORAGE_KEY = "admin_session";

const API_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:8999"
  : "";  // Use relative URLs in production (same origin)

// Hash the secret using SHA-256 before sending
async function hashSecret(secret) {
  const encoder = new TextEncoder();
  const data = encoder.encode(secret);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// Login with password, returns session token if valid
async function loginWithPassword(password) {
  try {
    const hashedPassword = await hashSecret(password);
    const res = await fetch(`${API_URL}/verify_admin`, {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ secret: hashedPassword }),
    });
    if (res.ok) {
      const data = await res.json();
      return data.token; // Server returns a session token
    }
    return null;
  } catch {
    return null;
  }
}

// Logout by invalidating session token on server
async function logoutSession(token) {
  try {
    await fetch(`${API_URL}/logout`, {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  } catch {
    // Ignore errors, we'll clear local state anyway
  }
}

export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [token, setToken] = useState(""); // Session token (not password!)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on mount
    const checkSession = () => {
      const storedToken = localStorage.getItem(STORAGE_KEY);
      if (storedToken) {
        // We trust the token exists - server will reject if invalid
        setIsAdmin(true);
        setToken(storedToken);
      }
      setLoading(false);
    };
    checkSession();
  }, []);

  const login = async (inputPassword) => {
    const sessionToken = await loginWithPassword(inputPassword);
    if (sessionToken) {
      // Store only the session token (random, not derived from password)
      localStorage.setItem(STORAGE_KEY, sessionToken);
      setIsAdmin(true);
      setToken(sessionToken);
      return true;
    }
    return false;
  };

  const logout = async () => {
    // Invalidate on server
    if (token) {
      await logoutSession(token);
    }
    localStorage.removeItem(STORAGE_KEY);
    setIsAdmin(false);
    setToken("");
  };

  return (
    <AdminContext.Provider value={{ isAdmin, token, login, logout, loading }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
};
